import type { LastBotState } from "../core/state";
import type { LayoutHint, Message } from "../core/types";

export const activeSubstantiveAssistant = (messages: readonly Message[]): Message | null => {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (message.role !== "assistant") continue;
    if (message.is_finished !== true) continue;
    if (message.metadata?.field_name === "tool_call" || message.metadata?.field_name === "submit_rating") continue;
    return message;
  }
  return null;
};

export const activeSubstantiveAssistantSelector = (state: LastBotState): Message | null =>
  activeSubstantiveAssistant(state.thread?.messages ?? []);

export const activeLayoutHintForMessages = (messages: readonly Message[]): LayoutHint | null => {
  const hint = activeSubstantiveAssistant(messages)?.metadata?.layout_hint;
  return hint && (hint.min_width || hint.min_height) ? hint : null;
};

/**
 * Size hint for the CURRENT answer: the popup tracks the most recent finished
 * assistant reply and shrinks back once the conversation moves on. The backend
 * sets `layout_hint` on messages carrying a product card or mini calendar, so a
 * rich answer grows the popup and a following plain answer resets it to the CSS
 * defaults. We scan back to the latest substantive assistant reply, ignoring
 * unfinished (still-streaming) replies, transient tool-call / rating messages,
 * and any trailing user message. Returns that reply's hint, or null when it
 * carries none. See the resize effect in ChatWidget (LAS-1653).
 */
export const activeLayoutHintSelector = (state: LastBotState): LayoutHint | null => {
  return activeLayoutHintForMessages(state.thread?.messages ?? []);
};

/**
 * The baseline `postcss-rem-to-pixel` used at build time. `propList: ['*']` makes
 * it rewrite every declaration including custom properties, so the widget's own
 * `--lastbot-widget-width: 26rem` ships as a literal `416px`.
 */
const REM_BASELINE_PX = 16;

/**
 * Normalise a hint length so it does not depend on the host page's root font
 * size.
 *
 * A value injected at runtime lands on the shadow host as-is, and `rem` there
 * resolves against the EMBEDDING page's root font size — while the widget's own
 * defaults were already compiled to absolute pixels. On a page using the common
 * `html { font-size: 62.5% }` reset, a `34rem` hint computes to 340px, which is
 * *narrower* than the 416px default: the "grow to fit rich content" hint would
 * shrink the popup instead. Converting at the same baseline the build used keeps
 * the hint meaningful on any host. Other units are passed through untouched.
 */
export const normalizeHintLength = (value: string | null | undefined): string | undefined => {
  if (!value) return undefined;

  const match = /^\s*(\d*\.?\d+)rem\s*$/i.exec(value);
  if (!match) return value;

  return `${Number(match[1]) * REM_BASELINE_PX}px`;
};
